import React, { useEffect, useState } from 'react';
import { Agent, TwilioScript, fetchExotelAgents, fetchExotelScripts, createExotelAgent, updateExotelAgent, deleteExotelAgent } from '../utils/api';
import './VoiceAgents.css';

const MODELS = [
  'gemini-2.5-flash-native-audio-latest',
  'gemini-3.5-flash',
  'gemini-3.5-flash-lite',
  'gemini-2.5-flash',
  'gemini-2.0-flash',
];

const EMPTY_AGENT_FORM: Omit<Agent, 'id' | 'scriptName' | 'createdAt' | 'updatedAt'> = {
  name: '',
  exotelNumber: '',
  llmModel: 'gemini-2.5-flash-native-audio-latest',
  scriptId: '',
};

interface VoiceAgentsProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const VoiceAgents: React.FC<VoiceAgentsProps> = ({ userRole }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [scripts, setScripts] = useState<TwilioScript[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [agentForm, setAgentForm] = useState({ ...EMPTY_AGENT_FORM });
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  const loadAgentsList = async () => {
    setLoading(true);
    try {
      const agentList = await fetchExotelAgents();
      setAgents(agentList);
      const scriptList = await fetchExotelScripts();
      setScripts(scriptList);
    } catch (err) {
      console.error('Failed to load agents/scripts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgentsList();
  }, []);

  const startAgentEdit = (a: Agent) => {
    setEditingAgentId(a.id);
    setAgentForm({
      name: a.name,
      exotelNumber: a.exotelNumber || '',
      llmModel: a.llmModel,
      scriptId: a.scriptId || '',
    });
    setFormError('');
  };

  const startAgentCreate = () => {
    setEditingAgentId('__new__');
    setAgentForm({ ...EMPTY_AGENT_FORM });
    setFormError('');
  };

  const saveAgent = async () => {
    if (!agentForm.name.trim()) {
      setFormError('Agent Name is required.');
      return;
    }
    setFormSaving(true);
    setFormError('');
    try {
      const data = {
        name: agentForm.name,
        exotelNumber: agentForm.exotelNumber ? agentForm.exotelNumber.trim() : null,
        llmModel: agentForm.llmModel,
        scriptId: agentForm.scriptId || null,
      };

      if (editingAgentId === '__new__') {
        await createExotelAgent(data);
      } else if (editingAgentId) {
        await updateExotelAgent(editingAgentId, data);
      }
      setEditingAgentId(null);
      await loadAgentsList();
    } catch (e: any) {
      setFormError(e.message || 'Save failed');
    } finally {
      setFormSaving(false);
    }
  };

  const deleteAgent = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this voice agent?')) return;
    try {
      await deleteExotelAgent(id);
      await loadAgentsList();
      if (editingAgentId === id) setEditingAgentId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="agents-view-container">
      {/* Left List Panel */}
      <div className="agents-list-panel">
        <div className="panel-title-bar">
          <h4>Voice Agents ({agents.length})</h4>
          <button className="add-agent-btn" onClick={startAgentCreate}>
            + Add Agent
          </button>
        </div>

        {loading ? (
          <div className="panel-empty-state">Loading agents...</div>
        ) : agents.length === 0 ? (
          <div className="panel-empty-state">No voice agents created yet. Click Add Agent to start.</div>
        ) : (
          <ul className="agent-list-items">
            {agents.map((a) => (
              <li
                key={a.id}
                className={`agent-list-item-card${editingAgentId === a.id ? ' active' : ''}`}
                onClick={() => startAgentEdit(a)}
              >
                <div className="agent-item-details">
                  <strong>{a.name}</strong>
                  <span className="agent-item-meta">
                    ExoPhone: {a.exotelNumber || <span className="unbound-number-badge">Unbound</span>}
                  </span>
                  <span className="agent-item-meta">
                    Script: {a.scriptName || <span className="no-script-badge">None</span>}
                  </span>
                </div>
                <button className="delete-item-action-btn" onClick={(e) => deleteAgent(a.id, e)}>
                  🗑️
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right Form Panel */}
      <div className="agents-form-panel">
        <div className="panel-title-bar">
          <h4>
            {editingAgentId === '__new__'
              ? 'Add New Voice Agent'
              : editingAgentId
              ? 'Edit Voice Agent'
              : 'Select an Agent'}
          </h4>
        </div>

        {editingAgentId ? (
          <div className="agent-form-body">
            <div className="form-group-custom">
              <label>Agent Name *</label>
              <input
                type="text"
                value={agentForm.name}
                onChange={(e) => setAgentForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sales Agent"
                className="form-input-field"
              />
            </div>

            <div className="form-group-custom">
              <label>Exotel Virtual Phone Number (ExoPhone)</label>
              <input
                type="text"
                value={agentForm.exotelNumber || ''}
                onChange={(e) => setAgentForm((f) => ({ ...f, exotelNumber: e.target.value }))}
                placeholder="e.g. +911141168263"
                className="form-input-field"
              />
              <span className="field-hint-text">
                Incoming calls to this ExoPhone number will trigger this agent dynamically.
              </span>
            </div>

            <div className="form-group-custom">
              <label>Google LLM Model Model Selection</label>
              <select
                value={agentForm.llmModel}
                onChange={(e) => setAgentForm((f) => ({ ...f, llmModel: e.target.value }))}
                className="form-select-field"
              >
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group-custom">
              <label>Assigned Script Template</label>
              <select
                value={agentForm.scriptId || ''}
                onChange={(e) => setAgentForm((f) => ({ ...f, scriptId: e.target.value }))}
                className="form-select-field"
              >
                <option value="">— Choose Script Template —</option>
                {scripts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {formError && <div className="form-error-alert-label">{formError}</div>}

            <div className="form-actions-buttons-row">
              <button
                className="form-action-btn secondary"
                onClick={() => setEditingAgentId(null)}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button className="form-action-btn primary" onClick={saveAgent} disabled={formSaving}>
                {formSaving ? 'Saving...' : editingAgentId === '__new__' ? 'Create Agent' : 'Update Agent'}
              </button>
            </div>
          </div>
        ) : (
          <div className="form-empty-state-card">
            <span className="empty-state-icon">🤖</span>
            <p>Select or add an agent to map prompts to virtual ExoPhone numbers.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAgents;
